import { Icon, Stack, Text, TooltipHost } from "@fluentui/react";
import { ReactNode } from "react";

import { formatDateTimeShort } from "@/utils/FormatDate";
import formatRelativeTime from "@/utils/FormatRelativeTime";

interface IStepInfo {
  friendlyName: string;
}

interface IUserInfo {
  email: string;
  fullName: string;
}

interface SharedLogRowProps {
  iconColor: string;
  title: string;
  statusChangeReason?: string;
  currentStep?: IStepInfo;
  nextStep?: IStepInfo;
  user?: IUserInfo;
  createdAt: string | Date;
  children?: ReactNode;
}

const RenderLogIcon = (color: string) => {
  return (
    <Icon
      iconName="StatusCircleRing"
      styles={{
        root: {
          fontSize: 18,
          fontWeight: 600,
          color: color
        }
      }}
    />
  );
};

const RenderStatusChangeReasonText = (statusChangeReason: string) => {
  return (
    <Stack>
      <Text variant="smallPlus" styles={{ root: { marginTop: 8 } }}>
        Motivo da alteração de Status:
      </Text>
      <Text variant="smallPlus">{statusChangeReason}</Text>
    </Stack>
  );
};

const RenderStepsInformation = (
  currentStep: IStepInfo,
  nextStep?: IStepInfo
) => {
  return (
    <Stack>
      <Stack horizontal tokens={{ childrenGap: 4 }}>
        <Text styles={{ root: { fontWeight: 600 } }}>Atual:</Text>
        <Text>{currentStep.friendlyName}</Text>
      </Stack>

      {nextStep && (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <Text styles={{ root: { fontWeight: 600 } }}>Próximo:</Text>
          <Text>{nextStep.friendlyName}</Text>
        </Stack>
      )}
    </Stack>
  );
};

const RenderLogMetadata = (createdAt: string | Date, user?: IUserInfo) => {
  const now = new Date();
  const logDate = new Date(createdAt);
  const createdAtFormatted = formatDateTimeShort(logDate);
  const relativeDate = formatRelativeTime(now, logDate);

  return (
    <Stack styles={{ root: { marginTop: 5 } }}>
      <Text styles={{ root: { fontSize: 12, color: "#888" } }}>
        {user?.email || "Email não disponível"}
      </Text>
      <Stack horizontal horizontalAlign="space-between">
        <Text>{user?.fullName || "Desconhecido"}</Text>
        <TooltipHost content={createdAtFormatted}>
          <Text>{relativeDate}</Text>
        </TooltipHost>
      </Stack>
    </Stack>
  );
};

export function SharedLogRow({
  iconColor,
  title,
  statusChangeReason,
  currentStep,
  nextStep,
  user,
  createdAt,
  children
}: SharedLogRowProps) {
  return (
    <Stack
      horizontal
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          padding: "15px 5px",
          borderBottom: "1px solid #ddd",
          alignItems: "center"
        }
      }}
    >
      {RenderLogIcon(iconColor)}
      <Stack styles={{ root: { flex: 1 } }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
          {title}
        </Text>
        {statusChangeReason && RenderStatusChangeReasonText(statusChangeReason)}

        {children}

        {currentStep && RenderStepsInformation(currentStep, nextStep)}

        {RenderLogMetadata(createdAt, user)}
      </Stack>
    </Stack>
  );
}
